/**
 * Password Migration Script
 *
 * Purpose: Migrate Shopify customer passwords to backend Firebase system
 * Trigger: After successful form-based login
 *
 * Security Notes:
 * - Password is NEVER logged to console
 * - Credentials stored temporarily in sessionStorage (cleared immediately after use)
 * - Only executes on successful Shopify authentication
 * - Uses HTTPS for all API calls
 *
 * Flow:
 * 1. On login page: Capture email/password from form submit
 * 2. Store in sessionStorage temporarily
 * 3. On account page: Detect successful login, execute migration
 * 4. Clear sessionStorage immediately
 * 5. Log result (excluding password)
 */

(function() {
  'use strict';

  // Backend API endpoint
  const MIGRATION_API_URL = '/apps/arztfx/syncShopifyUser';

  // sessionStorage keys (prefixed with w_ for consistency)
  const STORAGE_KEY_EMAIL = 'w_pw_mig_e';
  const STORAGE_KEY_PASSWORD = 'w_pw_mig_p';

  /**
   * Phase 1: Capture credentials on login page
   * Runs when user submits the login form
   */
  function captureLoginCredentials() {
    // Look for the customer login form by finding the form that contains CustomerEmail field
    const emailInput = document.getElementById('CustomerEmail');
    if (!emailInput) {
      return; // Not on login page
    }

    const loginForm = emailInput.closest('form');
    if (!loginForm) {
      return; // Form not found
    }

    console.log('[PasswordMigration] Login form detected, attaching listener');

    loginForm.addEventListener('submit', function() {
      const emailInput = document.getElementById('CustomerEmail');
      const passwordInput = document.getElementById('CustomerPassword');

      if (!emailInput || !passwordInput) {
        console.log('[PasswordMigration] Form fields not found');
        return;
      }

      const email = emailInput.value.trim();
      const password = passwordInput.value;

      // Only store if both fields have values
      if (email && password) {
        try {
          sessionStorage.setItem(STORAGE_KEY_EMAIL, email);
          sessionStorage.setItem(STORAGE_KEY_PASSWORD, password);
          console.log('[PasswordMigration] Credentials captured, pending migration after login');
        } catch (error) {
          console.error('[PasswordMigration] Failed to store credentials:', error.message);
        }
      }
    });
  }

  /**
   * Determine whether a Shopify customer is genuinely logged in.
   *
   * Authoritative signal: `window.w_customer_id`, injected from Liquid
   * (`{% if customer %}{{ customer.id }}{% endif %}`) by the account/login
   * templates. On the login page this is null; on the post-login account page
   * it holds the customer id.
   *
   * Fallback heuristic (when the global is absent): we must be on an `/account`
   * page that is NOT a login/register/reset/activate/logout sub-page, and the
   * login form must not be present.
   *
   * @returns {boolean}
   */
  function isCustomerLoggedIn() {
    if (typeof window.w_customer_id !== 'undefined') {
      return !!window.w_customer_id; // Authoritative when the template set it
    }

    const path = window.location.pathname;
    if (!path.includes('/account')) {
      return false;
    }
    if (/\/account\/(login|register|reset|activate|logout)/.test(path)) {
      return false; // Unauthenticated account sub-pages
    }
    if (document.getElementById('CustomerEmail')) {
      return false; // Login form on the page => not logged in
    }
    return true;
  }

  /**
   * Phase 2: Execute migration on account page
   * Runs when user lands on account page after successful login
   */
  function checkAndExecuteMigration() {

    console.log('[PasswordMigration] checkAndExecuteMigration');

    // Only run when the customer is GENUINELY logged in.
    //
    // The previous check (`pathname.includes('/account')`) was also true on
    // `/account/login`, so the migration fired while the visitor was still on
    // the login page. At that point Shopify's App Proxy does not append
    // `logged_in_customer_id`, so the backend rightly rejects the request with
    // 401 INVALID_SESSION. The migration must only run on the authenticated
    // account page that Shopify redirects to *after* a successful login.
    if (!isCustomerLoggedIn()) {
      console.log('[PasswordMigration] Customer not logged in yet — deferring migration');
      return; // Not authenticated; credentials stay in sessionStorage for the post-login page load
    }

    // Check if we have pending migration
    const email = sessionStorage.getItem(STORAGE_KEY_EMAIL);
    const password = sessionStorage.getItem(STORAGE_KEY_PASSWORD);

    if (!email || !password) {
      return; // No pending migration
    }

    console.log('[PasswordMigration] Detected successful login, starting migration');

    // Clear credentials from storage immediately (security)
    sessionStorage.removeItem(STORAGE_KEY_EMAIL);
    sessionStorage.removeItem(STORAGE_KEY_PASSWORD);

    // Execute migration in background
    executeMigration(email, password);
  }

  /**
   * Execute the password migration API call
   * @param {string} email - User email
   * @param {string} password - User password
   */
  function executeMigration(email, password) {
    console.log('[PasswordMigration] Calling backend API...');

    // Make API call
    fetch(MIGRATION_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email: email,
        password: password
      })
    })
    .then(function(response) {
      if (!response.ok) {
        throw new Error('HTTP ' + response.status + ': ' + response.statusText);
      }
      return response.json();
    })
    .then(function(data) {
      if (data.success) {
        console.log('[PasswordMigration] Success:', data.action, '-', data.message);
      } else {
        console.warn('[PasswordMigration] Backend returned error:', data.error || 'Unknown error');
      }
    })
    .catch(function(error) {
      console.error('[PasswordMigration] API call failed:', error.message);
    });

    // Note: User proceeds regardless of migration success/failure
    // Migration will be retried on next login if it failed
  }

  /**
   * Initialize the migration script
   * Determines which phase to execute based on current page
   */
  function init() {
    console.log('[PasswordMigration] Initializing on path:', window.location.pathname);

    // Phase 1: Capture credentials on login page
    captureLoginCredentials();

    // Phase 2: Execute migration on account page
    checkAndExecuteMigration();
  }

  // Run initialization when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
